import React from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { getTokenValue, styled, useProps } from '@tamagui/core';
import type { WithMediaProps } from '@tamagui/core';

import { getRGBA } from '../../helpers/convertColor';
import { getOpacityTokenValue } from '../../helpers/tokens';
import { Box } from '../Box';
import { Text } from '../Text';

const BoxWithRgbaBackground = styled(Box, {
  variants: {
    backgroundColorVariant: (variant: 'default' | 'accentBrand' | 'black' | 'inverse') => {
      const { pill: pillTokens } = tokens;

      if (variant !== 'inverse') return { backgroundColor: pillTokens.color.background[variant] };

      const inverseBackgroundColor = getRGBA(
        getTokenValue(pillTokens.color.background.default, 'color'),
        getOpacityTokenValue(pillTokens.opacity.backgroundInverse),
      );

      return { backgroundColor: inverseBackgroundColor };
    },
  } as const,
});

/**
 * Props for the Pill component.
 */
export type PillProps = {
  /**
   * Label to display in the pill.
   */
  label: string;
  /**
   * Whether the pill should have fully rounded corners.
   */
  rounded?: boolean;
  /**
   * Size variant of the pill.
   */
  size: 'medium' | 'small' | 'xsmall';
  /**
   * Color variant of the pill.
   */
  variant?: 'default' | 'accentBrand' | 'black' | 'inverse';
  /**
   * Weight variant of the pill.
   */
  weight?: 'mediumWeight' | 'regularWeight';
  /**
   * Optional data-testid for testing purposes.
   */
  'data-testid'?: string;
} & WithMediaProps<{
  /**
   * Size variant of the pill.
   */
  size: 'medium' | 'small';
}>;

/**
 * Image carousel base component.
 * @param {PillProps} props - The props for the component.
 * @returns {React.JSX.Element} The rendered component.
 */
export const Pill: React.FC<PillProps> = ({
  label,
  rounded,
  variant = 'default',
  weight = 'regularWeight',
  'data-testid': dataTestId,
  ...props
}) => {
  const { pill: pillTokens } = tokens;
  const { size = 'medium' } = useProps(props);

  const sizeToken = size === 'xsmall' ? 'small' : size;

  return (
    <BoxWithRgbaBackground
      alignItems="center"
      justifyContent="center"
      backgroundColorVariant={variant}
      borderColor={pillTokens.color.border[variant]}
      borderRadius={rounded ? pillTokens.cornerRadius.rounded : pillTokens.cornerRadius[sizeToken]}
      borderWidth={pillTokens.borderWidth.default}
      height={size === 'xsmall' ? 'auto' : pillTokens.size.height[weight][`${sizeToken}Size`]}
      paddingBottom={
        size === 'medium'
          ? pillTokens.padding.medium.bottom[weight]
          : pillTokens.padding.small.topBottom[weight]
      }
      paddingHorizontal={pillTokens.padding[sizeToken].rightLeft}
      paddingTop={
        size === 'medium'
          ? pillTokens.padding.medium.top[weight]
          : pillTokens.padding.small.topBottom[weight]
      }
      width="max-content"
      data-testid={dataTestId}
    >
      <Text
        tag="span"
        typography={size === 'xsmall' ? '$bodyXsmall' : pillTokens.typography[weight][size]}
        fontWeight={weight === 'mediumWeight' ? '$medium' : '$regular'}
        color={pillTokens.color.content.label[variant === 'black' ? 'inverse' : variant]}
      >
        {label}
      </Text>
    </BoxWithRgbaBackground>
  );
};
